import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import ProductCard from "../components/ProductCard";
import { useAuth } from "../contexts/AuthContext";
import useNoIndex from "../hooks/useNoIndex";
import Wholesale from "./Wholesale";

/**
 * /wholesale-shop — mirror of Shop, but pulls wholesale-priced variants only.
 * Auth-gated to approved wholesale customers (and admins).
 */
export default function WholesaleShop() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [config, setConfig] = useState({ min_kits: 5, kit_size: 10 });
  const [err, setErr] = useState("");
  useNoIndex();

  const approved = !!(user?.wholesale_approved || user?.role === "admin");

  useEffect(() => {
    if (!approved) return;
    api.get("/wholesale/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/wholesale/products")
      .then(({ data }) => setProducts(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load wholesale catalog."));
  }, [approved]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.category)))],
    [products]
  );

  if (!user) {
    return <GateMessage title="Sign in required" body="Please sign in to access wholesale pricing." />;
  }
  if (!approved) {
    return <Wholesale />;
  }

  const filtered = products.filter(
    (p) =>
      (filter === "All" || p.category === filter) &&
      (query === "" || p.name.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="wholesale-shop-page">
      <div className="overline">WHOLESALE CATALOG</div>
      <h1 className="font-heading text-4xl sm:text-5xl mt-3 font-light">
        Bulk Research Compounds
      </h1>
      <p className="text-sm text-[#8A94A6] mt-3 max-w-xl">
        Volume pricing for verified labs and distributors. All wholesale items are sold by the <span className="text-white">kit</span> — 1 kit ={" "}
        <span className="text-white font-mono-tech">{config.kit_size} vials</span> of the same SKU.
        Minimum order:{" "}
        <span className="text-white font-mono-tech">{config.min_kits} kits total</span>
        {" "}— mix &amp; match any products.
      </p>

      {err && (
        <div className="mt-6 p-4 border border-red-400/30 bg-red-500/5 text-red-300 text-sm">
          {err}
        </div>
      )}

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between border-y border-white/10 py-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`wholesale-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] border transition-colors ${
                filter === c
                  ? "border-[#00C3FF] text-[#00C3FF]"
                  : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <input
          data-testid="wholesale-shop-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search compounds..."
          className="input-tech max-w-xs"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 mt-px">
        {filtered.map((p) => (
          <ProductCard key={p.id} product={p} wholesale />
        ))}
      </div>

      {filtered.length === 0 && !err && (
        <div className="text-center text-[#8A94A6] py-20 font-mono-tech text-sm">
          {products.length === 0
            ? "Wholesale pricing is being finalized. Please check back shortly."
            : "No compounds match your search."}
        </div>
      )}
    </div>
  );
}

function GateMessage({ title, body, cta }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center">
      <div className="surface p-12">
        <div className="overline">WHOLESALE</div>
        <h1 className="font-heading text-3xl mt-3">{title}</h1>
        <p className="text-sm text-[#8A94A6] mt-4 leading-relaxed">{body}</p>
        {cta && (
          <Link
            to={cta.to}
            className="btn-primary mt-8 inline-flex"
            data-testid="wholesale-gate-cta"
          >
            {cta.label}
          </Link>
        )}
      </div>
    </div>
  );
}
